const SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:,.<>?";
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

/** Common whole passwords (exact match, case-insensitive). */
const COMMON_PASSWORDS = new Set(
  [
    "password",
    "password1",
    "password12",
    "password123",
    "12345678",
    "123456789",
    "1234567890",
    "qwertyui",
    "qwerty123",
    "abcdefgh",
    "abcdefghi",
    "letmein1",
    "welcome1",
    "admin123",
    "admin@123",
    "test@123",
    "test1234",
    "test12345",
    "pass@123",
    "user@123",
    "demo@123",
    "login@123",
    "iloveyou",
    "monkey12",
    "dragon12",
    "master12",
    "login123",
    "passw0rd",
    "changeme",
    "p@ssw0rd",
    "p@ssword",
  ].map(p => p.toLowerCase())
);

/** Dictionary / trivial words that must not appear as the letter block in a password. */
const DICTIONARY_WORDS = new Set(
  [
    "test",
    "admin",
    "password",
    "pass",
    "user",
    "login",
    "welcome",
    "demo",
    "guest",
    "qwerty",
    "abc",
    "abcd",
    "abcdef",
    "master",
    "dragon",
    "monkey",
    "letmein",
    "iloveyou",
    "sunshine",
    "princess",
    "football",
    "baseball",
    "hello",
    "welcome1",
  ].map(w => w.toLowerCase())
);

export type PasswordPersonalContext = {
  username?: string;
  email?: string;
  phone?: string;
  name?: string;
  dateOfBirth?: string;
  currentPassword?: string;
};

export type PasswordRule = {
  id: string;
  /** Shown in the blue "Password Requirements" list */
  requirement: string;
  /** Shown in the red live-validation error list */
  error: string;
  /** When false, rule is informational only (e.g. change-password rules) */
  enforceable?: boolean;
  test: (password: string, ctx?: PasswordPersonalContext) => boolean;
};

function hasRepeatingChars(password: string, maxAllowed = 3): boolean {
  let count = 1;
  for (let i = 1; i < password.length; i++) {
    if (password[i] === password[i - 1]) {
      count += 1;
      if (count > maxAllowed) {
        return true;
      }
    } else {
      count = 1;
    }
  }
  return false;
}

/** Reject runs of 3+ sequential chars (e.g. 123, abc, 321). */
function hasLongSequentialPattern(password: string, maxAllowed = 2): boolean {
  const lower = password.toLowerCase();
  let asc = 1;
  let desc = 1;

  for (let i = 1; i < lower.length; i++) {
    const prev = lower.charCodeAt(i - 1);
    const curr = lower.charCodeAt(i);
    const isAlnum =
      ((prev >= 48 && prev <= 57) || (prev >= 97 && prev <= 122)) &&
      ((curr >= 48 && curr <= 57) || (curr >= 97 && curr <= 122));

    if (!isAlnum) {
      asc = 1;
      desc = 1;
      continue;
    }

    // Only treat as sequential within the same character class (digits or letters).
    const sameClass =
      (prev >= 48 && prev <= 57 && curr >= 48 && curr <= 57) ||
      (prev >= 97 && prev <= 122 && curr >= 97 && curr <= 122);

    if (!sameClass) {
      asc = 1;
      desc = 1;
      continue;
    }

    if (curr === prev + 1) {
      asc += 1;
      desc = 1;
      if (asc > maxAllowed) {
        return true;
      }
    } else if (curr === prev - 1) {
      desc += 1;
      asc = 1;
      if (desc > maxAllowed) {
        return true;
      }
    } else {
      asc = 1;
      desc = 1;
    }
  }
  return false;
}

function isCommonOrDictionaryPassword(password: string): boolean {
  const lower = password.toLowerCase();
  if (COMMON_PASSWORDS.has(lower)) {
    return true;
  }

  // Letter-only runs (e.g. "Test" in "Test@123")
  const letterRuns = lower.match(/[a-z]+/g) ?? [];
  if (letterRuns.some(run => DICTIONARY_WORDS.has(run))) {
    return true;
  }

  // Weak pattern: dictionary-like word + optional specials + simple digits (Test@123, Admin123)
  if (
    /^(test|admin|user|pass|demo|welcome|login|guest|qwerty|abc|password)[^a-z0-9]*\d{1,}$/i.test(
      password
    )
  ) {
    return true;
  }

  return false;
}

function normalizeForCompare(value: string): string {
  return value.trim().toLowerCase().replace(/[\s._+-]+/g, "");
}

function containsPersonalInfo(
  password: string,
  ctx?: PasswordPersonalContext
): boolean {
  if (!ctx) {
    return false;
  }
  const lower = password.toLowerCase();
  const compactPassword = normalizeForCompare(password);
  const parts = new Set<string>();

  const addPart = (raw?: string) => {
    if (!raw?.trim()) {
      return;
    }
    const cleaned = raw.trim().toLowerCase();
    if (cleaned.length >= 2) {
      parts.add(cleaned);
    }
    const compact = normalizeForCompare(raw);
    if (compact.length >= 2) {
      parts.add(compact);
    }

    // Email local-part (before @) — always check, even short names (2+)
    if (cleaned.includes("@")) {
      const local = cleaned.split("@")[0] ?? "";
      if (local.length >= 2) {
        parts.add(local);
        parts.add(normalizeForCompare(local));
      }
    }

    for (const token of cleaned.split(/[\s@._+-]+/)) {
      if (token.length >= 2) {
        parts.add(token);
      }
    }
  };

  addPart(ctx.username);
  addPart(ctx.email);
  addPart(ctx.phone);
  addPart(ctx.name);
  addPart(ctx.dateOfBirth);

  for (const part of parts) {
    if (!part) {
      continue;
    }
    if (lower.includes(part) || compactPassword.includes(part)) {
      return true;
    }
  }
  return false;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: "length",
    requirement: "8 to 10 characters long",
    error: "Password must be at least 8 characters long",
    test: p => p.length >= 8 && p.length <= 10,
  },
  {
    id: "upper",
    requirement: "At least one UPPER CASE letter (A-Z)",
    error: "Password must contain at least one UPPER CASE letter (A-Z)",
    test: p => /[A-Z]/.test(p),
  },
  {
    id: "lower",
    requirement: "At least one lower case letter (a-z)",
    error: "Password must contain at least one lower case letter (a-z)",
    test: p => /[a-z]/.test(p),
  },
  {
    id: "number",
    requirement: "At least one number (0-9)",
    error: "Password must contain at least one number (0-9)",
    test: p => /[0-9]/.test(p),
  },
  {
    id: "special",
    requirement: `At least one special character: ${SPECIAL_CHARS}`,
    error: `Password must contain at least one special character: ${SPECIAL_CHARS}`,
    test: p => SPECIAL_CHARS_REGEX.test(p),
  },
  {
    id: "trim-spaces",
    requirement: "No spaces at the beginning or end",
    error: "Password must not have spaces at the beginning or end",
    test: p => p === p.trim() && !/\s/.test(p),
  },
  {
    id: "repeating",
    requirement: "No more than 3 repeating characters in a row",
    error: "Password must not have more than 3 repeating characters in a row",
    test: p => !hasRepeatingChars(p, 3),
  },
  {
    id: "sequential",
    requirement: "No sequential patterns longer than 3 characters",
    error:
      "Password must not contain sequential patterns longer than 3 characters",
    // Reject 3+ sequential runs (123, abc, etc.)
    test: p => !hasLongSequentialPattern(p, 2),
  },
  {
    id: "common",
    requirement: "Not a common password or dictionary word",
    error: "Password must not be a common password or dictionary word",
    test: p => !isCommonOrDictionaryPassword(p),
  },
  {
    id: "personal",
    requirement:
      "Cannot contain your username, email, phone, name, or date of birth",
    error:
      "Password cannot contain your username, email, phone, name, or date of birth",
    test: (p, ctx) => !containsPersonalInfo(p, ctx),
  },
  {
    id: "different",
    requirement:
      "When changing password: Must be different from current password",
    error: "Password must be different from your current password",
    enforceable: false,
    test: (p, ctx) => {
      if (!ctx?.currentPassword) {
        return true;
      }
      return p !== ctx.currentPassword;
    },
  },
];

/** Max length for new-password inputs (matches 8–10 rule). */
export const PASSWORD_MAX_LENGTH = 10;

export function getFailedPasswordRules(
  password: string,
  ctx?: PasswordPersonalContext
): PasswordRule[] {
  return PASSWORD_RULES.filter(rule => {
    if (rule.enforceable === false && !ctx?.currentPassword) {
      return false;
    }
    if (rule.id === "personal") {
      const hasContext = Boolean(
        ctx?.username ||
          ctx?.email ||
          ctx?.phone ||
          ctx?.name ||
          ctx?.dateOfBirth
      );
      if (!hasContext) {
        return false;
      }
    }
    return !rule.test(password, ctx);
  }).map(rule => {
    if (rule.id === "length" && password.length > 10) {
      return {
        ...rule,
        error: "Password must be at most 10 characters long",
      };
    }
    if (rule.id === "trim-spaces" && /\s/.test(password)) {
      return {
        ...rule,
        error: "Password must not contain spaces",
      };
    }
    return rule;
  });
}

export function isPasswordValid(
  password: string,
  ctx?: PasswordPersonalContext
): boolean {
  if (!password) {
    return false;
  }
  return getFailedPasswordRules(password, ctx).length === 0;
}

export function getPasswordRequirementMessages(): string[] {
  return PASSWORD_RULES.map(r => r.requirement);
}
